import { useState, type ReactNode } from 'react';
import { ErrorState, Field, Modal, MoneyInput } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import { useCreateCheckoutLink, useKirvanoAccounts, useLinkGroups, useUpdateCheckoutLink } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import type { CheckoutLinkInput } from '@/lib/types';
import { KirvanoAccountPicker, KirvanoConnectionPanel } from './KirvanoConnection';
import { PickOrCreate } from './PickOrCreate';

/** O link em edição; sem ele, o modal cadastra um novo. */
export interface EditingCheckout extends CheckoutLinkInput {
  id: string;
}

interface Props {
  editing?: EditingCheckout;
  /** Abre já na Kirvano, nesta conta — a volta do "entrar com a Kirvano". */
  kirvanoAccountId?: string;
  onClose: () => void;
}

/**
 * Kirvano: conectada pela API, os produtos e as vendas entram sozinhos. MundPay:
 * link à mão, a venda entra sozinha pelo webhook. Outra: link à mão, só os cliques.
 */
type Platform = 'kirvano' | 'mundpay' | 'other';

/** O grupo dos links da MundPay — o mesmo nome que o servidor dá às vendas dela. */
const MUNDPAY_GROUP = 'MundPay';
/** O checkout da MundPay: checkout.mundpay.com/{id} (o do painel) ou pay.mundpay.com/{id} (o da documentação). */
/** Os grupos que só a leitura preenche — o servidor recusa link à mão neles (checkoutLinks.service.ts). */
const MANAGED_GROUP_RE = /^(cakto|dotpag|kirvano · .+)$/i;
const MUNDPAY_URL_RE = /^https:\/\/(checkout|pay)\.mundpay\.com\/[^\s?#/]+/i;

const NEW_GROUP_KEY = '__grupo-novo__';

const HTTPS_RE = /^https:\/\/[^\s/]+\.[^\s/]+/i;

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <section className="flow-step">
      <h3 className="flow-step-title">
        <span className="flow-step-num" aria-hidden="true">{number}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * Cadastro de um link de checkout (administrador), em passos: de onde é o
 * checkout; na Kirvano, conectar a conta — os produtos dela viram links
 * sozinhos —; nas outras plataformas, a plataforma e o produto. Ao salvar,
 * cada membro ganha o seu link rastreado, na hora.
 */
export function CheckoutLinkModal({ editing, kirvanoAccountId, onClose }: Props) {
  const toast = useToast();
  const create = useCreateCheckoutLink();
  const update = useUpdateCheckoutLink();
  const groups = useLinkGroups();
  // Editar é sempre de um link à mão: os da Kirvano se mantêm sozinhos.
  const [platformChoice, setPlatformChoice] = useState<Platform | null>(
    editing ? (editing.groupName === MUNDPAY_GROUP ? 'mundpay' : 'other') : kirvanoAccountId ? 'kirvano' : null,
  );
  const [accountChoice, setAccountChoice] = useState<string>(kirvanoAccountId ?? '');
  const [otherGroup, setOtherGroup] = useState(editing?.groupName ?? '');
  const [productName, setProductName] = useState(editing?.productName ?? '');
  const [title, setTitle] = useState(editing?.title ?? '');
  const [url, setUrl] = useState(editing?.url ?? '');
  const [price, setPrice] = useState(editing?.price ?? 0);
  const [errors, setErrors] = useState<{ groupName?: string; productName?: string; url?: string }>({});
  const busy = create.isPending || update.isPending;

  // Enquanto a leitura da conta roda no servidor (logo depois de conectar, ou o "atualizar agora"), relê.
  const accountsQuery = useKirvanoAccounts({
    pollWhile: (accounts) => accounts.some((item) => item.id === accountChoice && item.connected && (item.syncing || !item.lastSyncAt)),
  });
  const accountsData = accountsQuery.data;
  const kirvanoGroups = new Set((accountsData ?? []).map((item) => item.groupName));
  const platform = platformChoice;
  const account = accountsData?.find((item) => item.id === accountChoice);
  const groupName = platform === 'mundpay' ? MUNDPAY_GROUP : otherGroup;
  // Link à mão: a MundPay e as outras plataformas.
  const manual = platform === 'other' || platform === 'mundpay';

  // Os grupos das contas da Kirvano só se escolhem pela conta; o da MundPay, pelo cartão dela.
  // Os grupos mantidos pela leitura (Cakto, DotPag, contas da Kirvano) e o da MundPay não recebem link à mão aqui.
  const groupOptions = (groups.data ?? [])
    .map((group) => group.name)
    .filter((name) => !kirvanoGroups.has(name) && name !== MUNDPAY_GROUP && !MANAGED_GROUP_RE.test(name));
  const productsOf = (name: string) => groups.data?.find((group) => group.name === name)?.products ?? [];
  const loading = groups.isLoading || accountsQuery.isLoading;

  function choosePlatform(next: Platform) {
    // O produto é do grupo: entrar ou sair da MundPay (grupo fixo) limpa o produto que não é dele.
    const nextGroup = next === 'mundpay' ? MUNDPAY_GROUP : otherGroup;
    if (nextGroup !== groupName && !productsOf(nextGroup).includes(productName)) setProductName('');
    setPlatformChoice(next);
    setErrors({});
  }

  function validate() {
    const next: typeof errors = {};
    if (platform === 'other' && otherGroup.trim().length < 2) next.groupName = 'Escolha a plataforma, ou crie uma nova.';
    if (productName.trim().length < 2) next.productName = 'Escolha o produto, ou crie um novo.';
    if (!HTTPS_RE.test(url.trim())) next.url = 'Cole o link completo do checkout, começando com https://.';
    else if (platform === 'mundpay' && !MUNDPAY_URL_RE.test(url.trim())) next.url = 'Cole o link do checkout da MundPay: https://checkout.mundpay.com/…';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit() {
    if (busy || !manual || !validate()) return;
    const body: CheckoutLinkInput = { groupName: groupName.trim(), productName: productName.trim(), title: title.trim(), url: url.trim(), price: price > 0 ? price : null };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...body });
        toast.success('Link atualizado para todos os membros. Os endereços já enviados continuam os mesmos.');
      } else {
        await create.mutateAsync(body);
        toast.success('Link adicionado. Já aparece nos links de todos os membros, cada um com o seu código.');
      }
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }


  return (
    <Modal
      title={editing ? 'Editar link de checkout' : 'Adicionar link de checkout'}
      width={620}
      onClose={onClose}
      footer={
        <>
          {platform === 'kirvano' ? (
            <button type="button" className="btn btn-primary" onClick={onClose}>
              <IconCheck /> {account?.connected ? 'Concluir' : 'Fechar'}
            </button>
          ) : (
            <>
              <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy || !manual}>
                {busy ? <span className="spinner" /> : <IconCheck />} {editing ? 'Salvar' : 'Adicionar para todos'}
              </button>
            </>
          )}
        </>
      }
    >
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 22 }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {loading ? (
          <div className="muted" role="status" style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="spinner" /> Carregando…
          </div>
        ) : accountsQuery.error ? (
          <ErrorState message={(accountsQuery.error as Error).message} />
        ) : (
          <>
            {editing ? null : (
            <Step number={1} title="De onde é o checkout?">
              <fieldset className="choice-cards" aria-label="Plataforma do checkout">
                <label className="choice-card">
                  <input type="radio" name="platform" checked={platform === 'kirvano'} onChange={() => choosePlatform('kirvano')} />
                  <span className="choice-card-title">Kirvano</span>
                  <span className="choice-card-sub">Entre com a conta: os produtos viram links sozinhos e a venda paga pelo link do membro entra no ranking.</span>
                </label>
                <label className="choice-card">
                  <input type="radio" name="platform" checked={platform === 'mundpay'} onChange={() => choosePlatform('mundpay')} />
                  <span className="choice-card-title">MundPay</span>
                  <span className="choice-card-sub">Cole o link do checkout: a venda paga pelo link do membro entra sozinha pelo webhook, no ranking.</span>
                </label>
                <label className="choice-card">
                  <input type="radio" name="platform" checked={platform === 'other'} onChange={() => choosePlatform('other')} />
                  <span className="choice-card-title">Outra plataforma</span>
                  <span className="choice-card-sub">Hotmart, Kiwify, página própria… Cole o link; os cliques são contados, mas a venda não entra sozinha.</span>
                </label>
              </fieldset>
              <p className="muted" style={{ fontSize: 12.5, margin: '8px 0 0' }}>
                Cakto e DotPag não precisam de cadastro: os produtos entram sozinhos, bloqueados. Para liberar, use o botão
                Bloqueada do link na aba da plataforma.
              </p>
            </Step>
            )}

            {platform === 'kirvano' && accountsData ? (
              <Step number={2} title="Conexão com a Kirvano">
                <KirvanoAccountPicker accounts={accountsData} value={accountChoice} onChange={setAccountChoice} />
                {account ? <KirvanoConnectionPanel account={account} /> : null}
              </Step>
            ) : null}

            {platform === 'other' ? (
              <Step number={editing ? 1 : 2} title="Plataforma">
                <PickOrCreate
                  label="Plataforma *"
                  value={otherGroup}
                  options={groupOptions}
                  onChange={(value) => {
                    setOtherGroup(value);
                    // O produto é do grupo: trocar para um grupo que já existe limpa um produto que não é dele.
                    // Digitar um grupo novo não limpa — senão cada tecla apagaria o produto novo já digitado.
                    if (groupOptions.includes(value) && !productsOf(value).includes(productName)) setProductName('');
                    setErrors((current) => ({ ...current, groupName: undefined }));
                  }}
                  placeholder="Escolha a plataforma"
                  newLabel="+ Nova plataforma…"
                  newPlaceholder="Nome da plataforma, ex.: Hotmart"
                  maxLength={40}
                  error={errors.groupName}
                  hint="Vira o grupo dos links na página."
                />
              </Step>
            ) : null}

            {manual ? (
              <Step number={(editing ? 1 : 2) + (platform === 'other' ? 1 : 0)} title="Produto e link">
                <div className="grid grid2">
                  <PickOrCreate
                    // Remonta só ao trocar de grupo existente, não a cada tecla de um grupo novo.
                    // A MundPay é um grupo fixo (fora de groupOptions): remonta nele também, sem herdar o produto de outro grupo.
                    key={platform === 'mundpay' || groupOptions.includes(groupName) ? groupName : NEW_GROUP_KEY}
                    label="Produto *"
                    value={productName}
                    options={productsOf(groupName)}
                    onChange={(value) => {
                      setProductName(value);
                      setErrors((current) => ({ ...current, productName: undefined }));
                    }}
                    placeholder="Escolha o produto"
                    newLabel="+ Novo produto…"
                    newPlaceholder="Nome do produto, ex.: Mentoria VIP"
                    error={errors.productName}
                    hint="Agrupa os links do mesmo produto."
                  />
                  <Field label="Descrição" hint="Opcional — ex.: Lançamento, Parcelado.">
                    <input className="inp" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={80} />
                  </Field>
                </div>

                <Field
                  label="Link do checkout *"
                  error={errors.url}
                  hint={
                    editing
                      ? 'Trocar o destino não muda o link que os membros já mandaram.'
                      : platform === 'mundpay'
                        ? 'No painel da MundPay: Produtos → o produto → Checkouts. Cada membro recebe um link próprio, com o código dele nas UTMs.'
                        : 'O endereço para onde o lead vai ao clicar. Cada membro recebe um link próprio, com o código dele.'
                  }
                >
                  <input
                    className="inp"
                    type="url"
                    inputMode="url"
                    value={url}
                    onChange={(event) => {
                      setUrl(event.target.value);
                      setErrors((current) => ({ ...current, url: undefined }));
                    }}
                    placeholder={platform === 'mundpay' ? 'https://checkout.mundpay.com/…' : 'https://pay.exemplo.com/checkout/…'}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={2000}
                  />
                </Field>

                <div className="grid grid2">
                  <Field
                    label="Valor (R$)"
                    hint={platform === 'mundpay' ? 'Opcional — deixe vazio se o preço não é fixo ou se a oferta é em dólar.' : 'Opcional — deixe vazio se o preço não é fixo.'}
                  >
                    <MoneyInput value={price} onChange={setPrice} onEnter={() => void submit()} />
                  </Field>
                </div>
              </Step>
            ) : null}
          </>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
